import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar, Footer } from "../components";
import { useAuth } from "../context/AuthContext";
import api from "../utils/base_url";
import '../styles/OrderHistory.css';


const OrderHistory = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const fetchOrders = useCallback(
    async (pageNo = 1) => {
      if (!user?.customer_details?.id) return;
      setLoading(true);
      try {
        const res = await api.get(
          `orders-history/${user.customer_details.id}/?page=${pageNo}`
        );
        const result = res.data.results || [];
        setOrders(result);
        setPage(pageNo);
        setTotalPages(Math.ceil(res.data.count / 16));
      } catch (error) {
        console.error("Error fetching order history:", error);
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  // Maps API status keys → CSS modifier class
  const statusClass = (status) => {
    const map = {
      pending: "pending",
      order_confirmed: "order_confirmed",
      shipped: "shipped",
      delivered: "delivered",
      cancelled: "cancelled",
      returned: "returned",
    };
    return map[status] || "pending";
  };

  const formatStatus = (status) => {
    if (!status) return "";
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  /* ── Skeleton loading state ── */
  const SkeletonGrid = () => (
    <div className="order-grid">
      {[...Array(10)].map((_, idx) => (
        <div key={idx} className="order-skeleton-card">
          <div className="order-skeleton-img" />
          <div className="order-skeleton-line w-80" />
          <div className="order-skeleton-line w-40" />
          <div className="order-skeleton-line w-60" />
        </div>
      ))}
    </div>
  );

  /* ── Empty state ── */
  const EmptyState = () => (
    <div className="order-empty-state">
      <div className="order-empty-icon">🛍️</div>
      <p className="order-empty-title">No orders yet</p>
      <p className="order-empty-text">
        When you place an order, it will appear here.
      </p>
      <Link to="/" className="btn btn-primary btn-sm">
        Start shopping
      </Link>
    </div>
  );

  /* ── Order grid ── */
  const OrderGrid = () => (
    <>
      <div className="order-grid" >
        {orders.map((order, idx) => {
          const firstItem = order.items?.[0];
          const imageUrl = firstItem?.product_image || "/no-image.png";

          return (
            <div key={idx} className="order-card" onClick={() => navigate(`/order-tracking/${order.order_number}`)}>
              {/* Product image */}
              <div className="order-card-image-wrap">
                <img
                  src={imageUrl}
                  alt={firstItem?.product_name || "Product"}
                  className="order-card-image"
                />
              </div>

              {/* Card body */}
              <div className="order-card-body">
                <p className="order-card-name">
                  {firstItem?.product_name || "Products included"}
                </p>

                <span className={`order-status-badge ${statusClass(order.status)}`}>
                  {formatStatus(order.status)}
                </span>

                <p className="order-card-number">#{order.order_number}</p>

                <p className="order-card-amount">
                  ₹{order.total_amount.toLocaleString()}
                </p>
              </div>

              {/* CTA */}
              <Link
                to={`/order-tracking/${order.order_number}`}
                className="order-card-btn"
              >
                View details
              </Link>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="order-pagination">
        <button
          className="order-pagination-btn"
          disabled={page === 1}
          onClick={() => fetchOrders(page - 1)}
        >
          ← Prev
        </button>

        <span className="order-pagination-info">
          {page} / {totalPages}
        </span>

        <button
          className="order-pagination-btn"
          disabled={page === totalPages}
          onClick={() => fetchOrders(page + 1)}
        >
          Next →
        </button>
      </div>
    </>
  );

  return (
    <>
      <Navbar />
      <div className="order-history-page">
        <div className="container">
          {/* Header */}
          <div className="order-history-header">
            <span className="order-history-eyebrow">Account</span>
            <h1 className="order-history-title">My Orders</h1>
          </div>

          <hr className="order-history-divider" />

          {/* Content */}
          {loading ? (
            <SkeletonGrid />
          ) : orders.length === 0 ? (
            <EmptyState />
          ) : (
            <OrderGrid />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OrderHistory;